import React, { useState, useEffect, useRef } from "react";
import { Search, Loader2, Mic2, Send, Check, Music4, Heart, Library, SendHorizonal, Star, Smile } from "lucide-react";
import { searchYouTube, createSubmission, fetchSettings, fetchYoutubeStats, createReaction } from "../lib/api";
import { classifyVersionLabels } from "../lib/labels";
import { LabelChips } from "../components/karaoke/LabelChips";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../components/ui/sheet";
import { toast } from "sonner";
import { Toaster } from "sonner";

const REACTION_EMOJIS = [
  { value: "🎉", label: "fiesta" },
  { value: "💃", label: "bailar" },
  { value: "😊", label: "feliz" },
  { value: "😢", label: "triste" },
  { value: "❤️", label: "corazón" },
  { value: "👏", label: "aplausos" },
];

const Guest = () => {
  const [singer, setSinger] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [ytResults, setYtResults] = useState([]);
  const [searchMeta, setSearchMeta] = useState({ forced: false, youtubeMessage: "" });
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(null);
  const [sentIds, setSentIds] = useState(new Set());
  const [confirmSong, setConfirmSong] = useState(null); // real result selected → confirm sheet
  const [confirmDedication, setConfirmDedication] = useState("");
  const [bottomPanel, setBottomPanel] = useState(null); // null | "reactions" | "rating"
  const [ratingValue, setRatingValue] = useState(0); // last star selection (1-5) for visual fill
  const [lastReaction, setLastReaction] = useState(null); // transient highlight
  const [guestYoutube, setGuestYoutube] = useState(true);
  const [catalogCount, setCatalogCount] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [requestedQueries, setRequestedQueries] = useState(new Set());
  const debounceRef = useRef(null);

  useEffect(() => {
    const stored = localStorage.getItem("guest_singer");
    if (stored) {
      setSinger(stored);
      setConfirmed(true);
    }
  }, []);

  // Poll the global "Guest YouTube Search" setting so toggles apply in near real-time
  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const s = await fetchSettings();
        if (active) setGuestYoutube(s.guest_youtube_search);
      } catch (e) { /* keep previous value */ }
    };
    load();
    const id = setInterval(load, 8000);
    return () => { active = false; clearInterval(id); };
  }, []);

  // Catalog size (reuses the existing unique cached songs counter)
  useEffect(() => {
    fetchYoutubeStats()
      .then((s) => setCatalogCount(s.unique_cached_songs_count ?? null))
      .catch(() => {});
  }, []);

  const abortRef = useRef(null);
  const lastNormalizedRef = useRef("");
  const inflightKeyRef = useRef(null); // guards against identical simultaneous searches

  const doSearch = async (rawQuery, confirmedSearch = false) => {
    const q = (rawQuery || "").trim();
    if (!q) {
      setResults([]);
      setYtResults([]);
      return;
    }
    // Auto (debounce) exige >= 4 caracteres; confirmada (Enter/Buscar) permite cortas.
    if (!confirmedSearch && q.length < 4) {
      setResults([]);
      setYtResults([]);
      return;
    }
    const normalized = q.toLowerCase().replace(/\s+/g, " ");
    if (!confirmedSearch && normalized === lastNormalizedRef.current) return;

    // De-dupe identical simultaneous searches (Enter + click, double click)
    const reqKey = `${confirmedSearch ? "F" : "A"}:${normalized}`;
    if (inflightKeyRef.current === reqKey) return;
    inflightKeyRef.current = reqKey;

    lastNormalizedRef.current = normalized;

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setHasSearched(true);
    try {
      // El botón/Enter (confirmada) fuerza una consulta nueva a YouTube (si está habilitado para invitados).
      const data = await searchYouTube(q, { maxResults: 12, signal: controller.signal, confirmed: confirmedSearch, guest: true, forceYoutube: confirmedSearch });
      setResults(data.results || []);
      setYtResults(data.youtubeResults || []);
      setSearchMeta({ forced: !!data.forced, youtubeMessage: data.youtubeMessage || "" });
    } catch (e) {
      if (e?.code === "ERR_CANCELED" || e?.name === "CanceledError") return;
      const msg =
        e?.response?.data?.detail ||
        (e?.response?.status === 429
          ? "Cuota de YouTube alcanzada. Intenta más tarde."
          : "Error al buscar");
      toast.error(msg);
    } finally {
      if (inflightKeyRef.current === reqKey) inflightKeyRef.current = null;
      if (abortRef.current === controller) abortRef.current = null;
      setLoading(false);
    }
  };

  const onSearchChange = (e) => {
    const v = e.target.value;
    setQuery(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!v.trim()) {
      setResults([]);
      setYtResults([]);
      setHasSearched(false);
      lastNormalizedRef.current = "";
      return;
    }
    debounceRef.current = setTimeout(() => doSearch(v), 1200);
  };

  const onSearchSubmit = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    lastNormalizedRef.current = ""; // force re-run
    doSearch(query, true); // confirmed: allows short queries, ignores debounce
  };

  const confirmName = () => {
    const name = singer.trim();
    if (!name) {
      toast.error("Por favor ingresa tu nombre");
      return;
    }
    localStorage.setItem("guest_singer", name);
    setConfirmed(true);
  };

  // Opens the confirm sheet for a real result (valid videoId). Only one bottom panel at a time.
  const openConfirm = (song) => {
    setBottomPanel(null);
    setConfirmDedication("");
    setConfirmSong(song);
  };

  const openReactions = () => {
    setConfirmSong(null);
    setBottomPanel("reactions");
  };

  const openRating = () => {
    setConfirmSong(null);
    setBottomPanel("rating");
  };

  const submitSong = async (song, dedication) => {
    setSubmitting(song.videoId);
    try {
      await createSubmission({
        videoId: song.videoId,
        title: song.title,
        channelTitle: song.channelTitle,
        thumbnail: song.thumbnail,
        duration: song.duration,
        singer,
        dedication: (dedication || "").trim() || null,
      });
      setSentIds((prev) => new Set([...prev, song.videoId]));
      setConfirmSong(null);
      toast.success("¡Enviada al anfitrión!", {
        description: "Espera la confirmación para que entre a la cola.",
      });
    } catch (e) {
      toast.error("Error al enviar la canción");
    } finally {
      setSubmitting(null);
    }
  };

  // Live reaction (emoji). Immediate send, repeatable, no cooldown. Reuses createReaction.
  const sendReaction = async (kind, value) => {
    setLastReaction(`${kind}-${value}`);
    try {
      await createReaction({ kind, value: String(value), fromName: singer });
    } catch (_) {
      toast.error("No se pudo enviar la reacción");
    } finally {
      setTimeout(() => setLastReaction(null), 350);
    }
  };

  // 1-5 stars → internal 1-10 scale (2/4/6/8/10). Immediate send, repeatable.
  const sendRating = (stars) => {
    setRatingValue(stars);
    sendReaction("vote", stars * 2);
  };

  const requestOffCatalog = async () => {
    const text = query.trim();
    if (!text) return;
    setRequesting(true);
    try {
      await createSubmission({
        videoId: "",
        title: text,
        channelTitle: "",
        thumbnail: "",
        duration: "",
        singer,
        dedication: null,
        offCatalog: true,
        query: text,
      });
      setRequestedQueries((prev) => new Set([...prev, text.toLowerCase()]));
      toast.success("¡Solicitud enviada al DJ!", {
        description: "El DJ decidirá si la busca durante el evento.",
      });
    } catch (e) {
      toast.error("No se pudo enviar la solicitud");
    } finally {
      setRequesting(false);
    }
  };

  const renderGuestRow = (r) => {
    const sent = sentIds.has(r.videoId);
    return (
      <div
        key={r.videoId}
        className="border-b border-border p-3"
        data-testid={`guest-result-${r.videoId}`}
      >
        <div className="flex items-start gap-3">
          <div className="relative shrink-0">
            <img src={r.thumbnail} alt="" className="w-24 h-14 object-cover rounded-sm bg-muted" />
            {r.duration && (
              <span className="absolute bottom-0.5 right-0.5 bg-black/85 text-[10px] font-mono px-1 rounded-sm">
                {r.duration}
              </span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold leading-snug line-clamp-2">{r.title}</p>
            <LabelChips labels={r.labels || classifyVersionLabels(`${r.title} ${r.channelTitle}`)} content={r.content_labels} className="mt-1" />
            <p className="text-xs text-muted-foreground mt-1 truncate">{r.channelTitle}</p>
          </div>
          <button
            onClick={() => !sent && openConfirm(r)}
            disabled={sent}
            data-testid={`guest-submit-${r.videoId}`}
            className={`shrink-0 h-9 px-3 rounded-md font-bold uppercase tracking-wide text-xs flex items-center gap-1.5 transition-colors ${
              sent
                ? "bg-secondary/20 text-secondary border border-secondary/40"
                : "bg-primary text-primary-foreground hover:bg-primary/90"
            } disabled:opacity-70`}
          >
            {sent ? (
              <>
                <Check className="w-3.5 h-3.5" /> Enviada
              </>
            ) : (
              <>
                <Send className="w-3.5 h-3.5" /> Seleccionar
              </>
            )}
          </button>
        </div>
      </div>
    );
  };

  if (!confirmed) {
    return (
      <div className="min-h-screen w-full bg-background text-foreground flex flex-col items-center justify-center px-6">
        <div className="w-full max-w-md text-center">
          <div className="w-16 h-16 rounded-full bg-primary/15 border border-primary/40 flex items-center justify-center mx-auto mb-6">
            <Mic2 className="w-8 h-8 text-primary" />
          </div>
          <h1 className="font-display text-4xl font-black uppercase tracking-tighter leading-tight">
            ¡Bienvenido <span className="text-primary neon-pink">cantante</span>!
          </h1>
          <p className="text-muted-foreground mt-3 mb-8 text-sm">
            Antes de elegir tu canción, dinos tu nombre. Aparecerá en la pantalla cuando sea tu turno.
          </p>
          <input
            type="text"
            value={singer}
            onChange={(e) => setSinger(e.target.value)}
            placeholder="Tu nombre"
            data-testid="guest-name-input"
            autoFocus
            onKeyDown={(e) => e.key === "Enter" && confirmName()}
            className="w-full bg-white/[0.06] border border-white/20 focus:border-primary focus:bg-white/[0.09] focus:outline-none focus:ring-2 focus:ring-primary/40 rounded-md px-4 h-14 font-mono text-center text-lg text-white placeholder:text-white/45 transition-all"
          />
          <button
            onClick={confirmName}
            data-testid="confirm-guest-name"
            className="w-full mt-4 h-14 rounded-md bg-primary text-primary-foreground font-bold uppercase tracking-wider glow-pink active:scale-95 transition-transform"
          >
            Comenzar
          </button>
          <p className="text-[10px] font-mono tracking-widest uppercase text-muted-foreground/50 mt-8">
            DJ Gabo Karaoke · Modo Invitado
          </p>
        </div>
        <Toaster position="top-center" theme="dark" />
      </div>
    );
  }

  return (
    <div className="h-[100dvh] w-full bg-background text-foreground flex flex-col overflow-hidden">
      <header className="shrink-0 z-10 bg-surface border-b border-border px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <Mic2 className="w-4 h-4 text-primary shrink-0" />
          <p className="font-display font-bold text-sm uppercase tracking-tight truncate">
            Hola <span className="text-primary">{singer}</span>
          </p>
        </div>
        <button
          onClick={() => {
            localStorage.removeItem("guest_singer");
            setConfirmed(false);
            setSinger("");
          }}
          className="text-xs font-mono uppercase tracking-widest text-muted-foreground hover:text-foreground"
        >
          Cambiar
        </button>
      </header>

      <div className="shrink-0 px-4 py-3 border-b border-border bg-surface/60">
        {!guestYoutube && (
          <div className="mb-3 rounded-md border border-secondary/30 bg-secondary/[0.07] px-3 py-2.5" data-testid="catalog-mode-banner">
            <p className="text-xs text-secondary flex items-center gap-1.5 font-medium">
              <Library className="w-3.5 h-3.5 shrink-0" />
              Estás buscando dentro del catálogo disponible del evento.
            </p>
            {catalogCount != null && (
              <p className="text-[11px] text-muted-foreground mt-1 font-mono">
                Canciones disponibles: <b className="text-foreground" data-testid="catalog-count">{catalogCount}</b>
              </p>
            )}
          </div>
        )}
        <p className="text-[10px] font-mono tracking-[0.25em] uppercase text-muted-foreground mb-2">
          /// Busca tu canción
        </p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSearchSubmit();
          }}
          className="relative flex items-center gap-2"
        >
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={query}
              onChange={onSearchChange}
              placeholder="Ej. Bohemian Rhapsody"
              data-testid="guest-search-input"
              className="w-full bg-white/[0.06] border border-white/20 hover:border-white/30 focus:border-primary focus:bg-white/[0.09] focus:outline-none focus:ring-2 focus:ring-primary/40 rounded-md pl-10 pr-9 h-12 font-mono text-sm text-white placeholder:text-white/55 transition-all"
            />
            {loading && (
              <Loader2 className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-primary animate-spin" />
            )}
          </div>
          <button
            type="submit"
            data-testid="guest-search-submit-button"
            className="shrink-0 h-12 px-3 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 active:scale-95 transition-all flex items-center justify-center"
            title="Buscar ahora"
          >
            <Search className="w-5 h-5" />
          </button>
        </form>
      </div>

      <div className="flex-1 overflow-y-auto min-h-0">
        {results.length === 0 && ytResults.length === 0 && !loading && !(hasSearched && query.trim()) && (
          <div className="flex flex-col items-center justify-center text-center p-10 text-muted-foreground">
            <Music4 className="w-10 h-10 mb-3 opacity-40" />
            <p className="text-sm">Busca tu canción favorita</p>
          </div>
        )}
        {results.length === 0 && ytResults.length === 0 && !loading && hasSearched && query.trim() && !guestYoutube && (
          <div className="flex flex-col items-center justify-center text-center p-8" data-testid="off-catalog-no-results">
            <Music4 className="w-10 h-10 mb-3 opacity-40 text-muted-foreground" />
            <p className="text-sm font-medium">No encontramos esa canción dentro del catálogo del evento.</p>
            <p className="text-xs text-muted-foreground mt-2 mb-5 max-w-xs">
              Si deseas solicitar una canción fuera del catálogo, puedes enviarla al DJ.
            </p>
            <button
              onClick={requestOffCatalog}
              disabled={requesting || requestedQueries.has(query.trim().toLowerCase())}
              data-testid="request-dj-button"
              className="h-11 px-5 rounded-md bg-primary text-primary-foreground font-bold uppercase tracking-wide text-xs flex items-center gap-2 glow-pink active:scale-95 transition-all disabled:opacity-60"
            >
              {requesting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : requestedQueries.has(query.trim().toLowerCase()) ? (
                <><Check className="w-4 h-4" /> Solicitud enviada</>
              ) : (
                <><SendHorizonal className="w-4 h-4" /> Solicitar al DJ</>
              )}
            </button>
          </div>
        )}
        {results.length === 0 && ytResults.length === 0 && !loading && hasSearched && query.trim() && guestYoutube && (
          <div className="flex flex-col items-center justify-center text-center p-10 text-muted-foreground" data-testid="no-results">
            <Music4 className="w-10 h-10 mb-3 opacity-40" />
            <p className="text-sm">{searchMeta.forced && searchMeta.youtubeMessage ? searchMeta.youtubeMessage : "No se encontraron resultados."}</p>
          </div>
        )}

        {/* Grupo LOCAL (catálogo del evento) */}
        {searchMeta.forced && results.length > 0 && (
          <div className="px-4 py-1.5 bg-secondary/[0.08] border-b border-secondary/25 flex items-center gap-2" data-testid="guest-local-results-header">
            <Library className="w-3 h-3 text-secondary" />
            <p className="text-[10px] font-mono text-secondary/90 uppercase tracking-widest">Coincidencias del catálogo local</p>
          </div>
        )}
        {results.map((r) => renderGuestRow(r))}

        {/* Grupo NUEVO de YouTube (solo búsqueda forzada con YouTube habilitado) */}
        {searchMeta.forced && ytResults.length > 0 && (
          <div className="px-4 py-1.5 bg-primary/[0.09] border-y border-primary/25 flex items-center gap-2" data-testid="guest-youtube-results-header">
            <Search className="w-3 h-3 text-primary" />
            <p className="text-[10px] font-mono text-primary/90 uppercase tracking-widest">Resultados nuevos de YouTube</p>
          </div>
        )}
        {searchMeta.forced && ytResults.map((r) => renderGuestRow(r))}

        {/* Nota discreta cuando la búsqueda forzada no trae nada nuevo */}
        {searchMeta.forced && ytResults.length === 0 && searchMeta.youtubeMessage && results.length > 0 && guestYoutube && (
          <p className="px-4 py-2 text-[11px] font-mono text-muted-foreground/70" data-testid="guest-youtube-no-new-note">
            {searchMeta.youtubeMessage}
          </p>
        )}
      </div>

      {/* Barra inferior compacta: un solo panel abierto a la vez */}
      <div className="shrink-0 border-t border-border bg-surface/90 backdrop-blur-md px-3 py-2.5 grid grid-cols-2 gap-2" data-testid="guest-bottom-bar">
        <button
          onClick={openReactions}
          data-testid="open-reactions-button"
          className="h-12 rounded-md bg-white/[0.06] border border-white/15 hover:border-primary/60 active:scale-95 transition-all flex items-center justify-center gap-2 font-bold uppercase tracking-wide text-xs text-white"
        >
          <span className="text-lg leading-none">😊</span> Reaccionar
        </button>
        <button
          onClick={openRating}
          data-testid="open-rating-button"
          className="h-12 rounded-md bg-white/[0.06] border border-white/15 hover:border-secondary/60 active:scale-95 transition-all flex items-center justify-center gap-2 font-bold uppercase tracking-wide text-xs text-white"
        >
          <Star className="w-4 h-4 text-amber-400" fill="currentColor" /> Votar
        </button>
      </div>

      {/* Panel: confirmar canción (SELECCIONAR → dedicatoria → ENVIAR CANCIÓN) */}
      <Sheet open={!!confirmSong} onOpenChange={(o) => !o && setConfirmSong(null)}>
        <SheetContent side="bottom" className="bg-surface border-border rounded-t-2xl" data-testid="confirm-song-sheet">
          <SheetHeader>
            <SheetTitle className="font-display text-xl">Confirmar canción</SheetTitle>
            <SheetDescription>Revisa la canción y añade una dedicatoria opcional.</SheetDescription>
          </SheetHeader>
          {confirmSong && (
            <div className="mt-4 space-y-4">
              <div className="flex gap-3 p-3 rounded-md bg-background border border-border">
                <div className="relative shrink-0">
                  <img src={confirmSong.thumbnail} alt="" className="w-24 h-14 object-cover rounded-sm bg-muted" />
                  {confirmSong.duration && (
                    <span className="absolute bottom-0.5 right-0.5 bg-black/85 text-[10px] font-mono px-1 rounded-sm">
                      {confirmSong.duration}
                    </span>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold line-clamp-2 leading-tight">{confirmSong.title}</p>
                  <LabelChips labels={confirmSong.labels || classifyVersionLabels(`${confirmSong.title} ${confirmSong.channelTitle}`)} content={confirmSong.content_labels} className="mt-1" />
                  <p className="text-xs text-muted-foreground mt-1 truncate">{confirmSong.channelTitle}</p>
                </div>
              </div>
              <div className="relative">
                <Heart className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-primary/70" />
                <input
                  type="text"
                  value={confirmDedication}
                  onChange={(e) => setConfirmDedication(e.target.value)}
                  placeholder="Dedicatoria (opcional)"
                  maxLength={120}
                  data-testid="confirm-dedication-input"
                  className="w-full bg-white/[0.05] border border-white/15 focus:border-primary focus:outline-none rounded-md pl-9 pr-3 h-11 text-sm font-mono text-white placeholder:text-white/45 transition-colors"
                />
              </div>
              <button
                onClick={() => submitSong(confirmSong, confirmDedication)}
                disabled={submitting === confirmSong.videoId}
                data-testid="confirm-send-song-button"
                className="w-full h-12 rounded-md bg-primary text-primary-foreground font-bold uppercase tracking-wide glow-pink active:scale-95 transition-transform flex items-center justify-center gap-2 disabled:opacity-70"
              >
                {submitting === confirmSong.videoId ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <><SendHorizonal className="w-4 h-4" /> Enviar canción</>
                )}
              </button>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Panel: reacciones en vivo */}
      <Sheet open={bottomPanel === "reactions"} onOpenChange={(o) => !o && setBottomPanel(null)}>
        <SheetContent side="bottom" className="bg-surface border-border rounded-t-2xl" data-testid="reactions-sheet">
          <SheetHeader>
            <SheetTitle className="font-display text-xl flex items-center gap-2"><Smile className="w-5 h-5 text-primary" /> Reacciona en vivo</SheetTitle>
            <SheetDescription>Toca los emojis que quieras, cuantas veces quieras.</SheetDescription>
          </SheetHeader>
          <div className="mt-4 grid grid-cols-3 gap-3 pb-2">
            {REACTION_EMOJIS.map((e) => {
              const active = lastReaction === `emoji-${e.value}`;
              return (
                <button
                  key={e.value}
                  onClick={() => sendReaction("emoji", e.value)}
                  data-testid={`reaction-emoji-${e.label}`}
                  aria-label={e.label}
                  className={`h-16 rounded-xl flex items-center justify-center text-4xl transition-all active:scale-90 ${
                    active ? "bg-primary/30 border-2 border-primary scale-105" : "bg-white/[0.06] border border-white/15 hover:border-primary/50"
                  }`}
                >
                  {e.value}
                </button>
              );
            })}
          </div>
        </SheetContent>
      </Sheet>

      {/* Panel: calificación 1-5 estrellas */}
      <Sheet open={bottomPanel === "rating"} onOpenChange={(o) => !o && setBottomPanel(null)}>
        <SheetContent side="bottom" className="bg-surface border-border rounded-t-2xl" data-testid="rating-sheet">
          <SheetHeader>
            <SheetTitle className="font-display text-xl">¿Cómo estuvo la actuación?</SheetTitle>
            <SheetDescription>Toca una estrella para votar.</SheetDescription>
          </SheetHeader>
          <div className="mt-5 flex items-center justify-center gap-2 pb-4" data-testid="rating-stars">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                onClick={() => sendRating(n)}
                data-testid={`rating-star-${n}`}
                aria-label={`${n} estrella${n === 1 ? "" : "s"}`}
                className="active:scale-90 transition-transform p-1"
              >
                <Star
                  className={`w-11 h-11 ${n <= ratingValue ? "text-amber-400" : "text-white/25"}`}
                  fill={n <= ratingValue ? "currentColor" : "none"}
                />
              </button>
            ))}
          </div>
        </SheetContent>
      </Sheet>

      <Toaster position="top-center" theme="dark" />
    </div>
  );
};

export default Guest;
